import {
    Button,
    ModalBody,
    ModalDialog,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Table
} from '@flos/react-ui';
import type { CoverageTextItem } from '../../recoil/types/hippoTypes';
import { useHippoText } from '../../hooks/useHippoText';
import CoverageTextModalTableCell from './CoverageTextModalTableCell';

const CoverageTextModal = ({
    showModal,
    closeModal,
    coverageTextItem
}: {
    showModal: boolean;
    closeModal: () => void;
    coverageTextItem: CoverageTextItem;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const tableHeaderIncludedText = getHippoText('DSS-COVERAGE-MODAL-COVERS-HEADER');
    const tableHeaderExcludedText = getHippoText('DSS-COVERAGE-MODAL-DO-NOT-COVER-HEADER');
    const numberOfRows = Math.max(
        coverageTextItem.excluded?.items.length ?? 0,
        coverageTextItem.included?.items.length ?? 0
    );

    return (
        <ModalOverlay
            isOpen={showModal}
            onDismiss={() => {
                closeModal();
            }}
        >
            <ModalDialog
                closeIconHandler={() => {
                    closeModal();
                }}
            >
                <ModalHeader title={coverageTextItem.title}>
                    <p className="u-spacing-inset-m--stretch-max">{coverageTextItem.subtitle}</p>
                </ModalHeader>
                <ModalBody>
                    {coverageTextItem.customerDescription && (
                        <p>{coverageTextItem.customerDescription}</p>
                    )}
                    <Table wrapped={true} mobileDirection="vertical">
                        <Table.Body className="u-display-none u-display-table-row-group@xs tbody@xs">
                            <Table.Row>
                                <Table.HeadCell>{tableHeaderIncludedText}</Table.HeadCell>
                                <Table.HeadCell>{tableHeaderExcludedText}</Table.HeadCell>
                            </Table.Row>

                            {Array.from({ length: numberOfRows }).map((_, i) => (
                                <Table.Row key={i} className="u-border-supplement-grey-light">
                                    {coverageTextItem.included?.items[i] ? (
                                        <CoverageTextModalTableCell
                                            text={coverageTextItem.included?.items[i]}
                                            iconProps={{
                                                size: 'medium',
                                                shape: 'circle-check',
                                                className: 'has-fill is-green'
                                            }}
                                        />
                                    ) : (
                                        <Table.Cell />
                                    )}
                                    {coverageTextItem.excluded?.items[i] ? (
                                        <CoverageTextModalTableCell
                                            text={coverageTextItem.excluded?.items[i]}
                                            iconProps={{
                                                size: 'medium',
                                                shape: 'circle-close',
                                                className: 'has-fill is-grey'
                                            }}
                                        />
                                    ) : (
                                        <Table.Cell />
                                    )}
                                </Table.Row>
                            ))}
                        </Table.Body>

                        <Table.Body className="u-display-none@xs tbody">
                            <Table.Row>
                                {coverageTextItem.included?.items.map((includedText) => (
                                    <CoverageTextModalTableCell
                                        dataHeader={tableHeaderIncludedText}
                                        key={includedText}
                                        text={includedText}
                                        iconProps={{
                                            size: 'medium',
                                            shape: 'circle-check',
                                            className: 'has-fill is-green'
                                        }}
                                    />
                                ))}
                            </Table.Row>
                            <Table.Row>
                                {coverageTextItem.excluded?.items.map((excludedText) => (
                                    <CoverageTextModalTableCell
                                        dataHeader={tableHeaderExcludedText}
                                        key={excludedText}
                                        text={excludedText}
                                        iconProps={{
                                            size: 'medium',
                                            shape: 'circle-close',
                                            className: 'has-fill is-grey'
                                        }}
                                    />
                                ))}
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </ModalBody>
                <ModalFooter className="u-text-left">
                    <Button
                        onClick={() => {
                            closeModal();
                        }}
                    >
                        Luk
                    </Button>
                </ModalFooter>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default CoverageTextModal;
